// Data-driven call-to-action builders.
//
// Given a raw v2 suggestion (+ the user's current edits), these derive the exact
// request the backend expects — endpoint path + body — WITHOUT the host having to
// hand-write per-archetype logic. The payload shape follows the apply contract
// (main contract §6.17 / the optimization-api-contract skill):
//
//   A / E / informational B,C  → { execute: true }
//   D / G (value change)        → { execute: true, value_after, execution_params: { currency } }
//   F (bulk over sub_items)     → { execute: true, execution_params: { selected_sub_item_ids } }
//   multi-item items[]          → { execute: true, execution_params: { item_ids } }  (forward-looking)
//   H (structural, requires_review) → { execute: true, execution_params: { confirm_review_done } }
//   C with action_type NO_ACTION    → no /apply; the CTA is "Investigar" (external URL)
//
// Pure functions — no React, no network.

const upper = (v) => String(v || '').toUpperCase();
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const VALUE_UNIT_SENTINELS = new Set(['none', 'ratio', 'seconds', '']);

/** Archetype-C alerts (action_type NO_ACTION) are investigated, not applied. */
export const isNoAction = (s) => upper(s?.action_type) === 'NO_ACTION';

/**
 * What the primary footer button should do, derived from the data:
 *   { kind: 'investigate', label, url }      — applicable === false (deep-link) or NO_ACTION alerts
 *   { kind: 'apply', label, requiresReview } — applicable actions (auto-apply via /apply)
 * `cta_label` (BE-provided: "Aplicar" | "Ver en Meta" | "Ver en Google") drives the label,
 * with graceful fallbacks for legacy payloads that omit it.
 */
export const resolvePrimaryCta = (suggestion) => {
  const s = suggestion || {};
  if (s.applicable === false || isNoAction(s)) {
    return {
      kind: 'investigate',
      label: s.cta_label || s.action_label || 'Investigar',
      // Prefer the entity's own deep link; fall back to the platform's ads manager.
      url: s.primary_entity?.permalink_url || buildInvestigateUrl(s),
    };
  }
  const requiresReview = upper(s.archetype) === 'H' && Boolean(s.structural_change?.requires_review);
  return { kind: 'apply', label: s.cta_label || 'Aplicar', requiresReview };
};

/**
 * Build the PATCH /apply request from the suggestion + the user's current edits.
 * @returns {{ id, path, body } | null} null when not auto-applicable (NO_ACTION or applicable === false).
 */
export const buildApplyRequest = (suggestion, opts = {}) => {
  const s = suggestion || {};
  const id = s.id;
  // Deep-link cards (applicable === false) and NO_ACTION alerts are never /apply'd.
  if (id == null || isNoAction(s) || s.applicable === false) return null;

  const archetype = upper(s.archetype);
  const body = { execute: true };
  const executionParams = {};

  const subItems = Array.isArray(s.sub_items) ? s.sub_items : [];
  const items = Array.isArray(s.items) ? s.items : [];
  const selected = Array.isArray(opts.selectedIds) ? opts.selectedIds.map(String) : null;

  if (subItems.length > 0) {
    // F: only the checked sub-items are acted on (empty → BE 422; caller should gate).
    executionParams.selected_sub_item_ids = selected ?? [];
  } else if (items.length > 1 && selected) {
    // Multi-item apply over a chosen subset (per-item apply is TBD in BE).
    executionParams.item_ids = selected;
  }

  // Value change: archetype D carries value_change_display; value-modifying B/C carry
  // the amount in the compat field value_after (value_change_display stays D-only).
  const vcd = isObj(s.value_change_display) ? s.value_change_display : null;
  if (vcd || s.value_after != null) {
    const fallbackAfter = vcd ? vcd.value_after : s.value_after;
    const edited = opts.valueAfter;
    body.value_after = (edited != null && edited !== '' && Number.isFinite(Number(edited)))
      ? Number(edited)
      : fallbackAfter;
    const unit = typeof s.value_unit === 'string' ? s.value_unit.trim() : '';
    executionParams.currency = vcd
      ? (vcd.currency ?? null)
      : (unit && !VALUE_UNIT_SENTINELS.has(unit.toLowerCase())
        ? unit
        : (s.metrics_extended?.currency ?? null));
  }

  if (archetype === 'H' && s.structural_change?.requires_review) {
    executionParams.confirm_review_done = Boolean(opts.confirmReviewDone);
  }

  if (Object.keys(executionParams).length > 0) body.execution_params = executionParams;
  return { id, path: `/api/suggestions/${id}/apply`, body };
};

/** Build the PATCH /dismiss request. `user_feedback` is optional. */
export const buildDismissRequest = (suggestion, opts = {}) => {
  const s = suggestion || {};
  const id = s.id;
  if (id == null) return null;
  const body = {};
  if (opts.userFeedback) body.user_feedback = String(opts.userFeedback);
  return { id, path: `/api/suggestions/${id}/dismiss`, body };
};

/** Build the PATCH /like request. BE requires user_feedback when isLiked === false. */
export const buildLikeRequest = (suggestion, opts = {}) => {
  const s = suggestion || {};
  const id = s.id;
  if (id == null) return null;
  const body = { is_liked: opts.isLiked == null ? null : Boolean(opts.isLiked) };
  if (opts.userFeedback) body.user_feedback = String(opts.userFeedback);
  return { id, path: `/api/suggestions/${id}/like`, body };
};

/**
 * Deep-link to a specific entity in Meta/Google Ads (§10.9). The BE does not provide
 * this; the FE builds it from the entity + platform. Returns the platform's ads-manager
 * URL even when external_id is missing (so a deep-link button is never dead); returns
 * null only for an unknown/blank platform.
 */
export function buildEntityDeepLink(entity, platform, accountId) {
  const plat = upper(platform);
  const ext = entity?.external_id;

  if (plat === 'META') {
    const base = 'https://www.facebook.com/adsmanager/manage/ads';
    const act = accountId ? `act=${encodeURIComponent(accountId)}` : '';
    if (!ext) return act ? `${base}?${act}` : base;
    const type = upper(entity?.type);
    const param = type === 'AD_GROUP'
      ? 'selected_adset_ids'
      : type === 'CAMPAIGN'
        ? 'selected_campaign_ids'
        : 'selected_ad_ids';
    return `${base}?${param}=${encodeURIComponent(ext)}` + (act ? `&${act}` : '');
  }
  if (plat === 'GOOGLE') {
    // customer_id is not in the payload today — fall back to the account overview.
    return 'https://ads.google.com/aw/overview';
  }
  return null;
}

/** Deep-link for the suggestion's primary entity (footer "Ver en {Platform}" CTA). */
export function buildInvestigateUrl(suggestion) {
  const s = suggestion || {};
  return buildEntityDeepLink(s.primary_entity, s.platform, s?.evidence?.account_id);
}
